import type { WebContext } from "@reboot-dev/reboot-web";
import { List } from "immutable";

import { type HttpRequestPage, HttpRequestPageSchema } from "@moment/api-collab/api-types";
import { type Store as DifferentialStore } from "@moment/api-collab/differential";
import {
	type Action,
	hydrateAction,
	rawActionSchema,
} from "@moment/api-collab/differential/QueryEvaluator/Action";
import { Logger } from "@moment/logging";
import { type Store as RebootStore, type TableChangesResponse } from "@moment/reboot-differential";

const logger = new Logger("request-page-utils");

/** Debounce interval for sending request page changes to collab (ms) */
const SEND_TO_COLLAB_DEBOUNCE_MS = 2000;

/**
 * Creates a sendToCollab callback that writes a request page to the store.
 *
 * CRITICALLY IMPORTANT, YET ALSO HACKY: DO NOT SEND TOO OFTEN. differential store still has no
 * `upsert` which means it is pretty racy because you need a delete/insert. If you mess this  up, it
 * is possible to accidentally delete the record without a corresponding insert, which is very bad!
 *
 * Yes, I know, I know. We will have to make this better. Sorry.
 */
export function makeRequestPageSendToCollabCommand(
	store: DifferentialStore<{ request: HttpRequestPage }>
): (request: HttpRequestPage) => Promise<void> {
	let inFlight = false;
	let pending: HttpRequestPage | null = null;
	let pendingResolvers: (() => void)[] = [];
	let pendingRejecters: ((err: unknown) => void)[] = [];
	let timeoutId: ReturnType<typeof setTimeout> | null = null;

	const send = async (request: HttpRequestPage) => {
		if (inFlight) {
			pending = request;
			return;
		}

		inFlight = true;
		try {
			await store.delete("request", "request");
			await store.insert("request", "request", request);
		} finally {
			inFlight = false;
			if (pending) {
				const next = pending;
				pending = null;
				await send(next);
			}
		}
	};

	return (request: HttpRequestPage): Promise<void> => {
		return new Promise((resolve, reject) => {
			pending = request;
			pendingResolvers.push(resolve);
			pendingRejecters.push(reject);

			if (timeoutId) {
				clearTimeout(timeoutId);
			}

			timeoutId = setTimeout(() => {
				const toResolve = [...pendingResolvers];
				const toReject = [...pendingRejecters];
				const toSend = pending;
				pendingResolvers = [];
				pendingRejecters = [];
				pending = null;
				timeoutId = null;

				if (!toSend) {
					toResolve.forEach((r) => r());
					return;
				}

				send(toSend)
					.then(() => toResolve.forEach((r) => r()))
					.catch((err) => {
						logger.error("Failed to send request page to collab", { err });
						toReject.forEach((r) => r(err));
					});
			}, SEND_TO_COLLAB_DEBOUNCE_MS);
		});
	};
}

/**
 * Parse TableChangesResponse into a list of Actions for reconciliation.
 */
function parseActions(value: TableChangesResponse): List<Action> {
	return List(value.changesJson).map((protoc) => {
		const parsed = rawActionSchema.parse(protoc.toJson());
		return hydrateAction(parsed);
	});
}

/**
 * Get the last epoch from received actions for version tracking.
 */
function getLastReceivedEpoch(receivedActions: List<Action>): number | undefined {
	const lastAction = receivedActions.last();
	if (!lastAction) {
		return undefined;
	}
	return lastAction.type === "queryRegistration"
		? lastAction.desiredStartEpoch
		: lastAction.desiredApplyEpoch;
}

/**
 * Get the latest request page from the store without creating a subscription.
 *
 * The raw store value is parsed through `HttpRequestPageSchema` to normalise
 * data written before the `type` discriminator was added (the schema's
 * `z.preprocess` step injects `type: "http"` when the field is missing).
 */
export function getLatestRequestPage(
	store: DifferentialStore<{ request: HttpRequestPage }>
): HttpRequestPage | undefined {
	const raw = store.latestState.tables.get("request")?.toArray()[0]?.tuple.getValue() as
		| HttpRequestPage
		| undefined;

	if (!raw) {
		return undefined;
	}

	const parsed = HttpRequestPageSchema.safeParse(raw);
	if (!parsed.success) {
		logger.warn("Failed to parse request page from collab store", {
			error: parsed.error,
		});
		return raw;
	}
	return parsed.data;
}

/**
 * Creates a remoteChanges async generator that subscribes to Reboot tableChanges
 * and reconciles actions into the DifferentialStore, then yields request page updates.
 *
 * This follows the same pattern as makeMetadataRemoteChangesCommand for metadata.
 */
export function makeRequestPageRemoteChangesCommand(
	rebootStoreRef: RebootStore.WeakReference,
	context: WebContext,
	initialVersion: number,
	store: DifferentialStore<{ request: HttpRequestPage }>
): (ops: { signal: AbortSignal }) => AsyncIterable<HttpRequestPage> {
	return async function* remoteChanges(ops: { signal: AbortSignal }) {
		const signal = ops.signal;

		let fromVersion = initialVersion - 1;
		const [stream, update] = await rebootStoreRef
			.reactively()
			.tableChanges(context, { fromVersion }, { signal });

		while (!signal.aborted) {
			const result = await stream.next();
			if (result.done || !result.value) {
				logger.info("Remote tableChanges stream ended");
				break;
			}

			logger.debug("Received tableChanges from Reboot", { fromVersion });

			const receivedActions = parseActions(result.value);
			await store.reconcileActions(receivedActions);

			// Get current request page after reconciliation and yield it
			const requestPage = getLatestRequestPage(store);
			if (requestPage) {
				yield requestPage;
			}

			// IF there is an action (there may not have been), AND there was a new version, update
			// the `fromVersion`.
			const lastHandledEpoch = getLastReceivedEpoch(receivedActions);
			const atLeastOneVersionWasHandled = lastHandledEpoch !== undefined;
			if (atLeastOneVersionWasHandled && lastHandledEpoch !== fromVersion) {
				fromVersion = lastHandledEpoch;

				// Update the stream to continue from the latest version
				update({ fromVersion });
			}
		}
	};
}
